/**
 * Slash command parser for chat input
 * Supports commands like:
 * - /roll 1d20+5 or /r 2d6
 * - /gmroll 1d20 or /gr 2d6
 * - /whisper @username message or /w @username message
 */

export interface ParsedCommand {
  type: 'chat' | 'roll' | 'whisper';
  content: string;
  whisperTo?: string[]; // user IDs or 'gm'
  notation?: string; // for dice rolls
  /**
   * Raw target tokens the user typed (with or without a leading `@`) that
   * did NOT resolve to a participant. Lets the input surface "no such
   * recipient" feedback instead of silently dropping the whisper.
   */
  unresolvedTargets?: string[];
}

interface Participant {
  userId: string;
  displayName: string;
  role: string;
}

/**
 * Parse a chat input string into a command
 */
export function parseSlashCommand(
  input: string,
  participants: Participant[],
  _currentUserId: string
): ParsedCommand {
  const trimmed = input.trim();

  // Check for dice roll commands
  if (trimmed.startsWith('/roll ') || trimmed.startsWith('/r ')) {
    const notation = trimmed.startsWith('/roll ')
      ? trimmed.slice(6).trim()
      : trimmed.slice(3).trim();

    return {
      type: 'roll',
      content: notation,
      notation,
    };
  }

  // Check for GM-only roll commands
  if (trimmed.startsWith('/gmroll ') || trimmed.startsWith('/gr ')) {
    const notation = trimmed.startsWith('/gmroll ')
      ? trimmed.slice(8).trim()
      : trimmed.slice(4).trim();

    // Find GM user ID
    const gm = participants.find((p) => p.role === 'gm');
    const whisperTo = gm ? [gm.userId] : [];

    return {
      type: 'roll',
      content: notation,
      notation,
      whisperTo,
    };
  }

  // Check for whisper commands
  if (trimmed.startsWith('/whisper ') || trimmed.startsWith('/w ')) {
    const content = trimmed.startsWith('/whisper ')
      ? trimmed.slice(9).trim()
      : trimmed.slice(3).trim();

    // Resolve a single token to a participant userId. Accepts the name with
    // or without a leading `@`, case-insensitively, and the `gm` alias.
    const resolve = (token: string): string | null => {
      const target = token.replace(/^@/, '').toLowerCase();
      if (!target) return null;
      if (target === 'gm') {
        return participants.find((p) => p.role === 'gm')?.userId ?? null;
      }
      return (
        participants.find((p) => p.displayName.toLowerCase() === target)
          ?.userId ?? null
      );
    };

    const parts = content.split(/\s+/).filter(Boolean);
    const mentions: string[] = [];
    const unresolvedTargets: string[] = [];

    // Walk the leading tokens as recipients. An explicit `@token` is always
    // treated as an intended target (resolved or not). A bare token only
    // counts as a target while it resolves to a participant — once a bare
    // token fails to resolve, the rest is the message body (so a plain
    // sentence isn't shredded). This makes `/w Name hello` work without `@`.
    let i = 0;
    for (; i < parts.length; i++) {
      const token = parts[i];
      const isExplicit = token.startsWith('@');
      const resolved = resolve(token);

      if (resolved) {
        if (!mentions.includes(resolved)) mentions.push(resolved);
        continue;
      }
      if (isExplicit) {
        unresolvedTargets.push(token.replace(/^@/, ''));
        continue;
      }
      // Bare, unresolved token: message body starts here.
      break;
    }

    const message = parts.slice(i).join(' ');

    return {
      type: 'whisper',
      content: message,
      whisperTo: mentions,
      ...(unresolvedTargets.length > 0 ? { unresolvedTargets } : {}),
    };
  }

  // Default: regular chat message
  return {
    type: 'chat',
    content: trimmed,
  };
}

/**
 * Check if input starts with a slash command
 */
export function isSlashCommand(input: string): boolean {
  const trimmed = input.trim();
  return trimmed.startsWith('/');
}

/**
 * Get autocomplete suggestions for slash commands
 */
export function getCommandSuggestions(input: string): string[] {
  const trimmed = input.trim().toLowerCase();

  const commands = [
    '/roll - Roll dice (e.g., /roll 1d20+5)',
    '/r - Short for /roll',
    '/gmroll - Roll dice visible only to GM',
    '/gr - Short for /gmroll',
    '/whisper - Send private message (e.g., /whisper @username hello)',
    '/w - Short for /whisper',
  ];

  if (!trimmed || trimmed === '/') {
    return commands;
  }

  return commands.filter((cmd) => cmd.toLowerCase().startsWith(trimmed));
}
